/** 에듀코디(LuckytemStudioClassLab) assignmentPrintUtils 포팅 — 과제 기록 HTML 프린트 */

import { displayClassMemberName } from './classMemberNameDisplay';

export const escapeHtml = (input: string) =>
  input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const formatAssignmentDueDate = (date: Date | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  const dayOfWeek = dayNames[d.getDay()];
  return `${month}/${day}(${dayOfWeek})`;
};

export const formatAssignmentDueDateFromIso = (iso: string | undefined) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return formatAssignmentDueDate(d);
};

export type AssignmentPrintStudent = {
  studentNumber?: string;
  studentName: string;
  status: string;
  memo: string;
};

export type AssignmentPrintTitleSource = {
  title: string;
};

export const sortStudentsForPrint = (students: AssignmentPrintStudent[]) => {
  return [...students].sort((a, b) => {
    const aNum = parseInt((a.studentNumber || '').replace(/\D/g, ''), 10);
    const bNum = parseInt((b.studentNumber || '').replace(/\D/g, ''), 10);
    if (!isNaN(aNum) && !isNaN(bNum) && aNum !== bNum) return aNum - bNum;
    return a.studentName.localeCompare(b.studentName, 'ko');
  });
};

export const buildGeneralAssignmentsExportHtml = (
  data: Array<{
    assignment: AssignmentPrintTitleSource;
    categoryName: string;
    dueDateText: string;
    basePoints: number;
    rewardCoins: number;
    rewardItemCardCount: number;
    students: AssignmentPrintStudent[];
  }>
) => {
  const colors = {
    pending: '#FF316B',
    confirmed: '#227971',
    delayed: '#65A994',
    exempted: '#FFCC6E',
    text: '#111111',
    border: '#d0d0d0',
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return '확인';
      case 'delayed':
        return '지연확인';
      case 'exempted':
        return '예외';
      case 'submitted':
        return '제출';
      case 'pending':
      default:
        return '미확인';
    }
  };

  const statusClass = (status: string) => {
    if (status === 'confirmed') return 'confirmed';
    if (status === 'delayed') return 'delayed';
    if (status === 'exempted') return 'exempted';
    return 'pending';
  };

  const sections = data
    .map(
      ({
        assignment,
        categoryName,
        dueDateText,
        basePoints,
        rewardCoins,
        rewardItemCardCount,
        students,
      }) => {
        const category = escapeHtml(categoryName || '전체 카테고리');
        const title = escapeHtml(assignment.title || '');
        const due = escapeHtml(dueDateText || '');

        const metaParts = [
          `마감일 ${due}`,
          `포인트 ${basePoints}`,
          `코인 ${rewardCoins}`,
          `아이템카드 ${rewardItemCardCount}`,
        ];

        const getStatusColor = (status: string) => {
          if (status === 'confirmed') return colors.confirmed;
          if (status === 'delayed') return colors.delayed;
          if (status === 'exempted') return colors.exempted;
          return colors.pending;
        };

        const rows = students
          .map((s) => {
            const num = s.studentNumber ? `${escapeHtml(s.studentNumber)} ` : '';
            const name = escapeHtml(s.studentName);
            const memo = escapeHtml(s.memo || '');
            const cls = statusClass(s.status);
            const label = escapeHtml(statusLabel(s.status));
            const statusColor = getStatusColor(s.status);
            return `
          <tr>
            <td class="student">${num}${name}</td>
            <td class="status">
              <span class="status-block ${cls}" style="background-color: ${statusColor} !important; background: ${statusColor} !important;"></span>
              <span class="status-label">${label}</span>
            </td>
            <td class="memo">${memo}</td>
          </tr>
        `;
          })
          .join('');

        return `
        <section class="assignment-page">
          <div class="category">${category}</div>
          <div class="title">${title}</div>
          <div class="meta">${metaParts.map(escapeHtml).join(' / ')}</div>
          <table class="table">
            <thead>
              <tr>
                <th class="student">학생</th>
                <th class="status">상태</th>
                <th class="memo">메모</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </section>
      `;
      }
    )
    .join('\n');

  return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>선택 과제 출력</title>
  <style>
    :root {
      --pending: ${colors.pending};
      --confirmed: ${colors.confirmed};
      --delayed: ${colors.delayed};
      --exempted: ${colors.exempted};
      --text: ${colors.text};
      --border: ${colors.border};
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", Arial, sans-serif;
      color: var(--text);
      background: #ffffff;
    }
    @page {
      size: A4;
      margin: 14mm;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    body {
      column-count: 2;
      column-gap: 12mm;
      column-fill: auto;
    }
    .assignment-page {
      padding: 0;
      margin-bottom: 16px;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .category { font-size: 12px; font-weight: 800; margin-bottom: 6px; }
    .title { font-size: 18px; font-weight: 900; margin-bottom: 6px; }
    .meta { font-size: 12px; color: #555; margin-bottom: 12px; }
    .table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: 1px solid ${colors.border};
      border-radius: 8px;
      overflow: hidden;
    }
    th, td {
      border-bottom: 1px solid ${colors.border};
      padding: 8px 6px;
      vertical-align: top;
      font-size: 12px;
    }
    th { background: #f5f5f5; text-align: left; font-weight: 800; }
    tr:last-child td { border-bottom: none; }
    th.student, td.student {
      width: 26%;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 0;
      padding: 8px 4px;
      letter-spacing: -0.04em;
    }
    th.status, td.status { width: 20%; white-space: nowrap; padding: 8px 4px; }
    th.memo, td.memo { width: 54%; white-space: pre-wrap; word-break: break-word; }
    .status-block {
      display: inline-block;
      width: 14px;
      height: 14px;
      border-radius: 4px;
      margin-right: 6px;
      vertical-align: -2px;
      border: 1px solid rgba(0,0,0,0.08);
      background-color: transparent;
    }
    .status-block.pending {
      background-color: #FF316B !important;
      background: #FF316B !important;
    }
    .status-block.confirmed {
      background-color: #227971 !important;
      background: #227971 !important;
    }
    .status-block.delayed {
      background-color: #65A994 !important;
      background: #65A994 !important;
    }
    .status-block.exempted {
      background-color: #FFCC6E !important;
      background: #FFCC6E !important;
    }
    .status-label { font-weight: 700; }
  </style>
</head>
<body>
  ${sections}
</body>
</html>`;
};

export const buildProjectAssignmentsExportHtml = (
  exportData: Array<{
    project: AssignmentPrintTitleSource;
    dueDateText: string;
    basePoints: number;
    rewardCoins: number;
    rewardItemCardCount: number;
    subTasks: Array<{
      subTaskTitle: string;
      subTaskPoints: number;
      students: AssignmentPrintStudent[];
    }>;
  }>
) => {
  const colors = {
    pending: '#FF316B',
    confirmed: '#227971',
    delayed: '#65A994',
    exempted: '#FFCC6E',
    border: '#d0d0d0',
    text: '#333333',
  };

  const statusClass = (status: string) => {
    if (status === 'confirmed') return 'confirmed';
    if (status === 'delayed') return 'delayed';
    if (status === 'exempted') return 'exempted';
    return 'pending';
  };

  const statusLabel = (status: string) => {
    if (status === 'confirmed') return '확인';
    if (status === 'delayed') return '지연';
    if (status === 'exempted') return '면제';
    return '미확인';
  };

  const sections = exportData
    .map((data) => {
      const { project, dueDateText, basePoints, rewardCoins, rewardItemCardCount, subTasks } =
        data;

      const subTaskSections = subTasks
        .map((subTask) => {
          const rows = subTask.students
            .map((s) => {
              const num = s.studentNumber ? `${escapeHtml(s.studentNumber)} ` : '';
              const name = escapeHtml(s.studentName);
              const memo = escapeHtml(s.memo || '');
              const cls = statusClass(s.status);
              const label = escapeHtml(statusLabel(s.status));
              const statusColor =
                s.status === 'confirmed'
                  ? colors.confirmed
                  : s.status === 'delayed'
                    ? colors.delayed
                    : s.status === 'exempted'
                      ? colors.exempted
                      : colors.pending;
              return `
            <tr>
              <td class="student">${num}${name}</td>
              <td class="status">
                <span class="status-block ${cls}" style="background-color: ${statusColor} !important; background: ${statusColor} !important;"></span>
                <span class="status-label">${label}</span>
              </td>
              <td class="memo">${memo}</td>
            </tr>
          `;
            })
            .join('');

          return `
          <div class="assignment-page">
            <div class="subtask-title">${escapeHtml(subTask.subTaskTitle)} (${subTask.subTaskPoints}점)</div>
            <table class="table">
              <thead>
                <tr>
                  <th class="student">학생</th>
                  <th class="status">상태</th>
                  <th class="memo">메모</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
          </div>
        `;
        })
        .join('');

      const rewardInfo: string[] = [];
      if (rewardCoins > 0) rewardInfo.push(`코인 ${rewardCoins}개`);
      if (rewardItemCardCount > 0) rewardInfo.push(`아이템카드 ${rewardItemCardCount}개`);

      return `
        <div class="assignment-page">
          <div class="category">프로젝트 과제</div>
          <div class="title">${escapeHtml(project.title)}</div>
          <div class="meta">${escapeHtml(dueDateText)} / 기본포인트 ${basePoints}점${
            rewardInfo.length > 0 ? ' / ' + rewardInfo.map(escapeHtml).join(' / ') : ''
          }</div>
          ${subTaskSections}
        </div>
      `;
    })
    .join('');

  return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>선택 프로젝트 과제 출력</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", Arial, sans-serif;
      color: ${colors.text};
      background: #ffffff;
    }
    @page {
      size: A4;
      margin: 14mm;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    body {
      column-count: 2;
      column-gap: 12mm;
      column-fill: auto;
    }
    .assignment-page {
      padding: 0;
      margin-bottom: 16px;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .category { font-size: 12px; font-weight: 800; margin-bottom: 6px; }
    .title { font-size: 18px; font-weight: 900; margin-bottom: 6px; }
    .meta { font-size: 12px; color: #555; margin-bottom: 12px; }
    .subtask-title { font-size: 14px; font-weight: 800; margin-bottom: 8px; color: #333; }
    .table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: 1px solid ${colors.border};
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 12px;
    }
    th, td {
      border-bottom: 1px solid ${colors.border};
      padding: 8px 6px;
      vertical-align: top;
      font-size: 12px;
    }
    th { background: #f5f5f5; text-align: left; font-weight: 800; }
    tr:last-child td { border-bottom: none; }
    th.student, td.student {
      width: 26%;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 0;
      padding: 8px 4px;
      letter-spacing: -0.04em;
    }
    th.status, td.status { width: 20%; white-space: nowrap; padding: 8px 4px; }
    th.memo, td.memo { width: 54%; white-space: pre-wrap; word-break: break-word; }
    .status-block {
      display: inline-block;
      width: 14px;
      height: 14px;
      border-radius: 4px;
      margin-right: 6px;
      vertical-align: -2px;
      border: 1px solid rgba(0,0,0,0.08);
      background-color: transparent;
    }
    .status-block.pending {
      background-color: #FF316B !important;
      background: #FF316B !important;
    }
    .status-block.confirmed {
      background-color: #227971 !important;
      background: #227971 !important;
    }
    .status-block.delayed {
      background-color: #65A994 !important;
      background: #65A994 !important;
    }
    .status-block.exempted {
      background-color: #FFCC6E !important;
      background: #FFCC6E !important;
    }
    .status-label { font-weight: 700; }
  </style>
</head>
<body>
  ${sections}
</body>
</html>`;
};

export const openAssignmentPrintWindow = (html: string) => {
  const win = window.open('', '_blank');
  if (!win) {
    alert('팝업이 차단되어 출력 창을 열 수 없습니다. 팝업 허용 후 다시 시도해주세요.');
    return false;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 300);
  return true;
};

export type AssignmentPrintMember = {
  id: string;
  name?: string;
  nameChoseongPositions?: number[];
  orderIndex: number;
};

export const buildPrintStudents = (
  assignedIds: string[],
  statuses: Array<{ studentId: string; status: string; memo?: string }>,
  members: AssignmentPrintMember[]
): AssignmentPrintStudent[] => {
  const byId = new Map(statuses.map((s) => [s.studentId, s]));
  // 동일 id가 여러 그룹에 있을 수 있음 — 이름이 있는 항목 우선
  const memberById = new Map<string, AssignmentPrintMember>();
  for (const m of members) {
    const prev = memberById.get(m.id);
    if (!prev || (!(prev.name || '').trim() && (m.name || '').trim())) {
      memberById.set(m.id, m);
    }
  }
  const students = assignedIds.map((id) => {
    const m = memberById.get(id);
    const st = byId.get(id);
    const name = (m ? displayClassMemberName(m) : '').trim();
    // 삭제·타학년 등 멤버 미매칭 시 raw id를 찍지 않음 (긴 id가 상태열과 겹침)
    return {
      studentNumber: m != null ? String(m.orderIndex).padStart(2, '0') : undefined,
      studentName: name || '(이름 없음)',
      status: st?.status || 'pending',
      memo: st?.memo || '',
    };
  });
  return sortStudentsForPrint(students);
};
